import { useAuthStore } from '@/stores/auth';
import { isMockSession } from '@/mocks';

// Relative path — proxied by rspack dev server (local) or nginx (production)
const API_BASE_URL = '/api';

interface RequestOptions {
	method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
	body?: unknown;
}

interface ApiError {
	error?: { code?: string; message: string };
}

async function request<T>(endpoint: string, options: RequestOptions = {}): Promise<T> {
	const { method = 'GET', body } = options;

	// Test-user session (staging demo): serve from in-memory fixtures, no API/DB.
	// Real SSO sessions pass through to the API as usual. The env check keeps
	// the mock chunk out of production bundles entirely.
	if (process.env.MOCK_MODE === 'true' && isMockSession()) {
		const { mockRequest } = await import('@/mocks/handlers');
		return mockRequest<T>(endpoint, method, body);
	}

	const token = useAuthStore.getState().token;

	const headers: Record<string, string> = {
		'Content-Type': 'application/json'
	};

	if (token) {
		headers['Authorization'] = `Bearer ${token}`;
	}

	const response = await fetch(`${API_BASE_URL}${endpoint}`, {
		method,
		headers,
		body: body ? JSON.stringify(body) : undefined
	});

	const data = await response.json() as (T & ApiError);

	if (!response.ok) {
		if (data.error?.code === 'ACCOUNT_BLOCKED') {
			useAuthStore.getState().logout();
			throw new Error(data.error?.message || 'Your account has been blocked.');
		}
		if (response.status === 401) {
			useAuthStore.getState().logout();
			window.location.href = '/login';
			throw new Error(data.error?.message || 'Session expired');
		}
		throw new Error(data.error?.message || 'Request failed');
	}

	return data;
}

async function uploadFile<T>(endpoint: string, file: File, fieldName = 'file'): Promise<T> {
	if (process.env.MOCK_MODE === 'true' && isMockSession()) {
		const { mockUpload } = await import('@/mocks/handlers');
		return mockUpload<T>(endpoint, file, fieldName);
	}

	const token = useAuthStore.getState().token;
	const formData = new FormData();
	formData.append(fieldName, file);

	const headers: Record<string, string> = {};
	if (token) {
		headers['Authorization'] = `Bearer ${token}`;
	}

	const response = await fetch(`${API_BASE_URL}${endpoint}`, {
		method: 'POST',
		headers,
		body: formData
	});

	const data = await response.json() as (T & ApiError);
	if (!response.ok) {
		if (response.status === 401) {
			useAuthStore.getState().logout();
			window.location.href = '/login';
		}
		throw new Error(data.error?.message || 'Upload failed');
	}
	return data;
}

// Auth
export const authApi = {
	login: (email: string, password: string) =>
		request<{ success: boolean; data: { user: any; token: string } }>('/auth/login', {
			method: 'POST',
			body: { email, password }
		}),

	register: (email: string, name: string, password: string) =>
		request<{ success: boolean; data: { user: any; token: string } }>('/auth/register', {
			method: 'POST',
			body: { email, name, password }
		}),

	googleLogin: (credential: string) =>
		request<{ success: boolean; data: { user: any; token: string } }>('/auth/google', {
			method: 'POST',
			body: { credential }
		}),

	me: () => request<{ success: boolean; data: any }>('/auth/me')
};

// Hiring Requests
export const hiringApi = {
	list: (page = 1, limit = 20, filters?: { search?: string; creatorId?: string; dealStatus?: string }) => {
		const params = new URLSearchParams({ page: String(page), limit: String(limit) });
		if (filters?.search?.trim()) params.set('search', filters.search.trim());
		if (filters?.creatorId && filters.creatorId !== 'all') params.set('creatorId', filters.creatorId);
		if (filters?.dealStatus && filters.dealStatus !== 'all') params.set('dealStatus', filters.dealStatus);
		return request<{ success: boolean; data: any[]; meta: any }>(`/hiring-requests?${params.toString()}`);
	},

	listCreators: () =>
		request<{ success: boolean; data: { id: string; name: string }[] }>('/hiring-requests/creators'),

	stats: () =>
		request<{ success: boolean; data: { active: number; draftJds: number; pendingFeedback: number; completed: number } }>('/hiring-requests/stats'),

	get: (id: string) =>
		request<{ success: boolean; data: any }>(`/hiring-requests/${id}`),

	create: (data: Record<string, unknown>) =>
		request<{ success: boolean; data: any }>('/hiring-requests', {
			method: 'POST',
			body: data
		}),

	update: (id: string, data: Record<string, unknown>) =>
		request<{ success: boolean; data: any }>(`/hiring-requests/${id}`, {
			method: 'PUT',
			body: data
		}),

	/** Move the request through the CRM pipeline — every hiring request IS a lead. */
	updateDealStatus: (id: string, dealStatus: string, lostReason?: string) =>
		request<{ success: boolean; data: any }>(`/hiring-requests/${id}/deal-status`, {
			method: 'PATCH',
			body: { dealStatus, ...(lostReason ? { lostReason } : {}) }
		}),

	updateCriteria: (id: string, criteria: any) =>
		request<{ success: boolean; data: any }>(`/hiring-requests/${id}/criteria`, {
			method: 'PUT',
			body: criteria
		}),

	generateCriteria: (id: string, additionalContext?: string) =>
		request<{ success: boolean; data: { jobId: string } }>(`/hiring-requests/${id}/criteria/generate`, {
			method: 'POST',
			body: additionalContext ? { additionalContext } : undefined
		}),

	getCriteriaHistory: (id: string) =>
		request<{ success: boolean; data: any[] }>(`/hiring-requests/${id}/criteria/history`),

	restoreCriteria: (id: string, version: number) =>
		request<{ success: boolean; data: any }>(`/hiring-requests/${id}/criteria/restore`, {
			method: 'POST',
			body: { version }
		}),

	generateJD: (id: string, options?: any) =>
		request<{ success: boolean; data: { jobId: string } }>(`/hiring-requests/${id}/generate-jd`, {
			method: 'POST',
			body: { options }
		}),

	generateModules: (id: string, moduleTypes: ModuleType[], includeCriteria = true, context?: string, criteriaContext?: string, moduleContexts?: Record<string, string>, meetingNotes?: string) =>
		request<{ success: boolean; data: { criteriaJobId: string | null; moduleJobIds: Record<string, string>; message: string } }>(
			`/hiring-requests/${id}/generate-modules`,
			{
				method: 'POST',
				body: { moduleTypes, includeCriteria, context, criteriaContext, moduleContexts, meetingNotes }
			}
		),

	confirmCriteria: (id: string) =>
		request<{ success: boolean; data: any }>(`/hiring-requests/${id}/confirm-criteria`, {
			method: 'POST'
		}),

	delete: (id: string) =>
		request<{ success: boolean }>(`/hiring-requests/${id}`, { method: 'DELETE' }),

	getFeedbackReview: (id: string) =>
		request<{ success: boolean; data: { comments: any[]; candidateFeedback: any[]; ratings: any[] } }>(`/hiring-requests/${id}/feedback-review`),

	updateStatus: (id: string, status: 'draft' | 'cancelled' | 'completed' | 'resume') =>
		request<{ success: boolean; data: any }>(`/hiring-requests/${id}/status`, {
			method: 'PATCH',
			body: { status }
		}),

	uploadContextFile: (id: string, file: File) =>
		uploadFile<{ success: boolean; data: { url: string; gcsPath: string; name: string; size: number; mimetype: string; uploadedAt: string } }>(
			`/hiring-requests/${id}/context-files`, file
		),

	deleteContextFile: (id: string, gcsPath: string) =>
		request<{ success: boolean }>(`/hiring-requests/${id}/context-files`, {
			method: 'DELETE',
			body: { gcsPath }
		}),

	share: (id: string, userIds: string[]) =>
		request<{ success: boolean; data: any }>(`/hiring-requests/${id}/share`, {
			method: 'POST',
			body: { userIds }
		}),

	removeShare: (id: string, userIds: string[]) =>
		request<{ success: boolean; data: any }>(`/hiring-requests/${id}/share`, {
			method: 'DELETE',
			body: { userIds }
		})
};

// Job Descriptions
export const jdApi = {
	list: (page = 1, limit = 20) =>
		request<{ success: boolean; data: any[]; meta: any }>(`/job-descriptions?page=${page}&limit=${limit}`),

	get: (id: string) =>
		request<{ success: boolean; data: any }>(`/job-descriptions/${id}`),

	update: (id: string, content: string, structuredContent: any) =>
		request<{ success: boolean; data: any }>(`/job-descriptions/${id}`, {
			method: 'PUT',
			body: { content, structuredContent }
		}),

	regenerate: (id: string, additionalInstructions?: string) =>
		request<{ success: boolean; data: { jobId: string } }>(`/job-descriptions/${id}/regenerate`, {
			method: 'POST',
			body: { additionalInstructions }
		})
};

// Feedback
export const feedbackApi = {
	list: () => request<{ success: boolean; data: any[] }>('/feedback'),

	create: (jdId: string, data: { jdVersionId: string; source: string; rawFeedback: string }) =>
		request<{ success: boolean; data: any }>(`/feedback/job-descriptions/${jdId}`, {
			method: 'POST',
			body: data
		}),

	summarize: (id: string) =>
		request<{ success: boolean; data: { jobId: string } }>(`/feedback/${id}/summarize`, {
			method: 'POST'
		})
};

// Companies
export const companyApi = {
	list: () => request<{ success: boolean; data: any[] }>('/companies'),

	get: (id: string) => request<{ success: boolean; data: any }>(`/companies/${id}`),

	create: (data: { name: string; industry?: string; mainIndustry?: string; subIndustry?: string; sizeRange?: string; website?: string; linkedinUrl?: string; additionalContext?: string; pics?: { name: string; email: string; title?: string; phone?: string }[] }) =>
		request<{ success: boolean; data: any }>('/companies', {
			method: 'POST',
			body: data
		}),

	update: (id: string, data: { name?: string; industry?: string; sizeRange?: string; website?: string; linkedinUrl?: string }) =>
		request<{ success: boolean; data: any }>(`/companies/${id}`, {
			method: 'PUT',
			body: data
		}),

	// Contacts (Hiring Manager PIC)
	listContacts: (companyId: string) =>
		request<{ success: boolean; data: any[] }>(`/companies/${companyId}/contacts`),

	addContact: (companyId: string, data: { contactId?: string; name?: string; email?: string; title?: string; phone?: string }) =>
		request<{ success: boolean; data: any }>(`/companies/${companyId}/contacts`, {
			method: 'POST',
			body: data
		}),

	updateContact: (companyId: string, contactId: string, data: any) =>
		request<{ success: boolean; data: any }>(`/companies/${companyId}/contacts/${contactId}`, {
			method: 'PUT',
			body: data
		}),

	removeContact: (companyId: string, contactId: string) =>
		request<{ success: boolean }>(`/companies/${companyId}/contacts/${contactId}`, { method: 'DELETE' }),

	generateContactLink: (companyId: string, contactId: string, hiringRequestId: string) =>
		request<{ success: boolean; data: any }>(`/companies/${companyId}/contacts/${contactId}/generate-link`, {
			method: 'POST',
			body: { hiringRequestId }
		}),

	generateCompanyLink: (companyId: string, cvType?: 'blind' | 'standard') =>
		request<{ success: boolean; data: any }>(`/companies/${companyId}/generate-link`, {
			method: 'POST',
			body: { cvType }
		}),

	listCompanyLinks: (companyId: string) =>
		request<{ success: boolean; data: any[] }>(`/companies/${companyId}/links`),

	// Company Research
	getResearch: (companyId: string) =>
		request<{ success: boolean; data: any }>(`/companies/${companyId}/research`),

	getResearchHistory: (companyId: string) =>
		request<{ success: boolean; data: any[] }>(`/companies/${companyId}/research/history`),

	generateResearch: (companyId: string, additionalContext?: string) =>
		request<{ success: boolean; data: any }>(`/companies/${companyId}/research/generate`, {
			method: 'POST',
			body: { additionalContext }
		}),

	updateResearch: (companyId: string, data: { content?: any; readableContent?: string; editReason?: string }) =>
		request<{ success: boolean; data: any }>(`/companies/${companyId}/research`, {
			method: 'PUT',
			body: data
		}),

	downloadResearchPDF: (companyId: string) => {
		const token = useAuthStore.getState().token;
		return fetch(`${API_BASE_URL}/companies/${companyId}/research/pdf`, {
			headers: { Authorization: `Bearer ${token}` }
		}).then(async res => {
			if (!res.ok) throw new Error('PDF download failed');
			const blob = await res.blob();
			const cd = res.headers.get('Content-Disposition') || '';
			const match = cd.match(/filename\*?=(?:UTF-8''|")?([^";]+)"?/i);
			const filename = match ? decodeURIComponent(match[1].replace(/"/g, '')) : null;
			return { blob, filename };
		});
	},

	restoreResearch: (companyId: string, version: number) =>
		request<{ success: boolean; data: any }>(`/companies/${companyId}/research/restore`, {
			method: 'POST',
			body: { version }
		})
};

// AI
export const aiApi = {
	usage: (period = '30d') =>
		request<{ success: boolean; data: any }>(`/ai/usage?period=${period}`),

	budget: () => request<{ success: boolean; data: any }>('/ai/budget')
};

// Modules (for hiring request modules)
export type ModuleType =
	| 'company_research'
	| 'deal_briefing'
	| 'standard_jd'
	| 'blind_jd'
	| 'interview_questions'
	| 'candidate_sourcing';

export const moduleApi = {
	// List all modules for a hiring request
	list: (hiringRequestId: string) =>
		request<{ success: boolean; data: any[] }>(`/hiring-requests/${hiringRequestId}/modules`),

	// Get specific module by type
	get: (hiringRequestId: string, moduleType: ModuleType) =>
		request<{ success: boolean; data: any }>(`/hiring-requests/${hiringRequestId}/modules/${moduleType}`),

	// Generate AI content for a module
	generate: (hiringRequestId: string, moduleType: ModuleType, additionalContext?: string, meetingNotes?: string) =>
		request<{ success: boolean; data: { jobId: string; message: string } }>(
			`/hiring-requests/${hiringRequestId}/modules/${moduleType}/generate`,
			{
				method: 'POST',
				body: { additionalContext, meetingNotes }
			}
		),

	// Update/edit module content
	update: (hiringRequestId: string, moduleType: ModuleType, data: { content?: Record<string, unknown>; readableContent?: string; editReason?: string }) =>
		request<{ success: boolean; data: any }>(
			`/hiring-requests/${hiringRequestId}/modules/${moduleType}`,
			{
				method: 'PUT',
				body: data
			}
		),

	// Get edit history for a module
	history: (hiringRequestId: string, moduleType: ModuleType) =>
		request<{ success: boolean; data: { module: any; editHistory: any[] } }>(
			`/hiring-requests/${hiringRequestId}/modules/${moduleType}/history`
		),

	// Restore module to specific version
	restore: (hiringRequestId: string, moduleType: ModuleType, version: number) =>
		request<{ success: boolean; data: any }>(
			`/hiring-requests/${hiringRequestId}/modules/${moduleType}/restore`,
			{
				method: 'POST',
				body: { version }
			}
		),

	// Download module as PDF (standard_jd and blind_jd only)
	downloadPDF: (hiringRequestId: string, moduleType: ModuleType) => {
		const token = useAuthStore.getState().token;
		return fetch(`${API_BASE_URL}/hiring-requests/${hiringRequestId}/modules/${moduleType}/pdf`, {
			headers: { Authorization: `Bearer ${token}` }
		}).then(res => {
			if (!res.ok) throw new Error('PDF download failed');
			return res.blob();
		});
	}
};

// Sourcing (SignalHire integration)
export interface SignalHireSearchParams {
	query?: string;
	titles?: string[];
	locations?: string[];
	skills?: string[];
	companies?: {
		include?: string[];
		exclude?: string[];
	};
	experience?: {
		min?: number;
		max?: number;
	};
	page?: number;
	limit?: number;
	requestId?: string;
	scrollId?: string;
}

export interface SourcedCandidate {
	id: string;
	hiringRequestId: string;
	signalhireId: string;
	name: string;
	email?: string;
	linkedinUrl?: string;
	currentTitle?: string;
	currentCompany?: string;
	location?: string;
	profileData: Record<string, unknown>;
	status: 'sourced' | 'contacted' | 'interviewing' | 'hired' | 'rejected' | 'withdrawn';
	recommended: boolean;
	rejectedReason?: string | null;
	rejectedBy?: 'us' | 'company' | 'candidate' | null;
	rejectedByUser?: string | null;
	rejectedAt?: string | null;
	cvFileUrl?: string;
	cvOriginalName?: string;
	outreachStatus?: string;
	hasInterviewNotes?: boolean;
	outreachCount?: number;
	pendingOutreachCount?: number;
	infoFetchedAt?: string | null;
	contactFetchedAt?: string | null;
	hasGeneratedStandardCV?: boolean;
	hasGeneratedBlindCV?: boolean;
	hasGeneratedConsultantNotes?: boolean;
	blindAlias?: string | null;
	createdAt: string;
}

export interface CandidateVaultDetail {
	id: string;
	signalhireId: string;
	fullName: string;
	location: string | null;
	currentTitle: string | null;
	currentCompany: string | null;
	skills: string[];
	experience: {
		position?: string;
		company?: string;
		location?: string | null;
		current?: boolean;
		started?: string | null;
		ended?: string | null;
		summary?: string | null;
		companyUrl?: string | null;
		companySize?: string | null;
		staffCount?: number | null;
		industry?: string | null;
		website?: string | null;
	}[];
	profileData: Record<string, unknown>;
	gender: string | null;
	photoUrl: string | null;
	headline: string | null;
	summary: string | null;
	education: {
		faculty?: string;
		university?: string;
		url?: string | null;
		startedYear?: number | null;
		endedYear?: number | null;
		degree?: string[];
	}[];
	languages: { name: string; proficiency?: string }[];
	organizations: { name: string; position?: string | null; startDate?: string | null; endDate?: string | null }[];
	contacts: {
		type: string;
		value: string;
		rating?: string;
		subType?: string;
		info?: string;
	}[];
	social: { type: string; link: string; rating?: string }[];
	courses: { name: string }[];
	projects: { name: string; description?: string | null; url?: string | null; startDate?: string | null; endDate?: string | null }[];
	certifications: { name: string; license?: string; authority?: string }[];
	patents: { name: string; issue?: string; patentNumber?: string | null; date?: string | null }[];
	publications: { name: string; description?: string | null; issue?: string | null; url?: string | null; date?: string | null }[];
	honorAwards: { name: string; description?: string | null; issue?: string | null; date?: string | null }[];
	detailedLocations: { name: string }[];
	infoStatus: string | null;
	contactStatus: string | null;
	infoFetchedAt: string | null;
	contactFetchedAt: string | null;
	createdAt: string;
	updatedAt: string;
}

export const sourcingApi = {
	// Search candidates via SignalHire
	search: (hiringRequestId: string, searchParams: SignalHireSearchParams) =>
		request<{ success: boolean; data: { profiles: any[]; total: number; requestId: string; scrollId: string }; isConfigured: boolean }>(
			'/sourcing/search',
			{
				method: 'POST',
				body: { hiringRequestId, searchParams }
			}
		),

	// Search by tier from AI-generated candidate_sourcing module
	searchByTier: (hiringRequestId: string, searchType: 'direct' | 'unconventional' | 'custom', searchIndex: number, parameterOverrides?: Record<string, unknown>) =>
		request<{
			success: boolean;
			data: { profiles: any[]; total: number; requestId: string; scrollId: string };
			searchInfo: { searchName: string; description: string; searchType: string; searchIndex: number; tier: number };
			isConfigured: boolean;
		}>(
			'/sourcing/search-by-tier',
			{
				method: 'POST',
				body: { hiringRequestId, searchType, searchIndex, ...(parameterOverrides ? { parameterOverrides } : {}) }
			}
		),

	// Get sourced candidates for a hiring request
	getCandidates: (hiringRequestId: string, options?: { status?: string; selectedOnly?: boolean }) => {
		const params = new URLSearchParams();
		if (options?.status) params.append('status', options.status);
		if (options?.selectedOnly) params.append('selectedOnly', 'true');
		return request<{ success: boolean; data: SourcedCandidate[] }>(
			`/sourcing/candidates/${hiringRequestId}?${params.toString()}`
		);
	},

	// Save candidate from search results
	saveCandidate: (hiringRequestId: string, candidate: any) =>
		request<{ success: boolean; data: SourcedCandidate }>('/sourcing/candidates', {
			method: 'POST',
			body: { hiringRequestId, candidate }
		}),

	// Update candidate status
	updateCandidate: (candidateId: string, updates: { status?: string; recommended?: boolean }) =>
		request<{ success: boolean; data: SourcedCandidate }>(`/sourcing/candidates/${candidateId}`, {
			method: 'PATCH',
			body: updates
		}),

	// Select candidate for interview
	selectForInterview: (candidateId: string) =>
		request<{ success: boolean; data: SourcedCandidate }>(`/sourcing/candidates/${candidateId}/select`, {
			method: 'POST'
		}),

	// Check SignalHire credits
	credits: () => request<{
		success: boolean;
		data: {
			search: { credits: number };
			contact: { credits: number };
		}
	}>('/sourcing/credits'),

	// Remove candidate
	removeCandidate: (candidateId: string) =>
		request<{ success: boolean }>(`/sourcing/candidates/${candidateId}`, {
			method: 'DELETE'
		}),

	// Request candidate information (profile data without contacts)
	requestCandidateInfo: (signalhireId: string) =>
		request<{ success: boolean; data: CandidateVaultDetail; message: string }>('/sourcing/candidate-vault/request-info', {
			method: 'POST',
			body: { signalhireId }
		}),

	// Request candidate contact information
	requestCandidateContacts: (signalhireId: string) =>
		request<{ success: boolean; data: CandidateVaultDetail; message: string }>('/sourcing/candidate-vault/request-contacts', {
			method: 'POST',
			body: { signalhireId }
		}),

	// Get candidate vault detail
	getCandidateVaultDetail: (signalhireId: string) =>
		request<{ success: boolean; data: CandidateVaultDetail }>(`/sourcing/candidate-vault/${encodeURIComponent(signalhireId)}`),

	// Unrecommend candidate (revert to interviewing)
	unrecommendCandidate: (candidateId: string) =>
		request<{ success: boolean; data: SourcedCandidate }>(`/sourcing/candidates/${candidateId}/unrecommend`, {
			method: 'POST'
		}),

	// Withdraw candidate
	withdrawCandidate: (candidateId: string) =>
		request<{ success: boolean; data: SourcedCandidate }>(`/sourcing/candidates/${candidateId}/withdraw`, {
			method: 'POST'
		}),

	// Reject candidate (with reason + who rejected) / revert a rejection
	rejectCandidate: (candidateId: string, body: { reason: string; rejectedBy: 'us' | 'company' | 'candidate' }) =>
		request<{ success: boolean; data: SourcedCandidate }>(`/sourcing/candidates/${candidateId}/reject`, {
			method: 'POST',
			body
		}),

	unrejectCandidate: (candidateId: string) =>
		request<{ success: boolean; data: SourcedCandidate }>(`/sourcing/candidates/${candidateId}/unreject`, {
			method: 'POST'
		}),

	// Hire candidate
	hireCandidate: (candidateId: string, startDate: string) =>
		request<{ success: boolean; data: any }>(`/sourcing/candidates/${candidateId}/hire`, {
			method: 'POST',
			body: { startDate }
		}),

	// Unhire candidate (revert to recommended)
	unhireCandidate: (candidateId: string) =>
		request<{ success: boolean; data: SourcedCandidate }>(`/sourcing/candidates/${candidateId}/unhire`, {
			method: 'POST'
		}),

	// Search existing candidates in vault by name/title/company
	searchVault: (query: string) =>
		request<{ success: boolean; data: Pick<CandidateVaultDetail, 'id' | 'signalhireId' | 'fullName' | 'currentTitle' | 'currentCompany' | 'location' | 'photoUrl'>[] }>(
			`/sourcing/candidate-vault/search?q=${encodeURIComponent(query)}`
		),

	// Add existing vault candidate to a hiring request pipeline
	addFromVault: (hiringRequestId: string, signalhireId: string) =>
		request<{ success: boolean; data: SourcedCandidate; message?: string }>('/sourcing/candidates/add-from-vault', {
			method: 'POST',
			body: { hiringRequestId, signalhireId }
		}),

	// Add candidate by LinkedIn URL (triggers SignalHire lookup)
	addByLinkedIn: (hiringRequestId: string, linkedinUrl: string) =>
		request<{ success: boolean; data: SourcedCandidate; message?: string }>('/sourcing/candidates/add-by-linkedin', {
			method: 'POST',
			body: { hiringRequestId, linkedinUrl }
		}),

	// Add a SignalHire-discovered candidate (search profile snapshot) to a pipeline
	addFromDiscovery: (hiringRequestId: string, signalhireId: string, profile: {
		fullName: string;
		location?: string | null;
		currentTitle?: string | null;
		currentCompany?: string | null;
		skills?: string[];
		experience?: { company: string; title: string }[];
		openToWork?: boolean;
	}) =>
		request<{ success: boolean; data: SourcedCandidate; message?: string }>('/sourcing/candidates/add-from-discovery', {
			method: 'POST',
			body: { hiringRequestId, signalhireId, profile }
		}),

	// Manually add a candidate without SignalHire
	addManualCandidate: (hiringRequestId: string, data: {
		name: string;
		currentTitle?: string;
		currentCompany?: string;
		location?: string;
		email?: string;
		phone?: string;
		linkedinUrl?: string;
	}) =>
		request<{ success: boolean; data: SourcedCandidate }>('/sourcing/candidates/add-manual', {
			method: 'POST',
			body: { hiringRequestId, ...data }
		}),

	// Upload CV for a candidate (admin)
	uploadCandidateCV: (candidateId: string, file: File) =>
		uploadFile<{ success: boolean; data: { cvFileUrl: string; cvOriginalName: string } }>(
			`/sourcing/candidates/${candidateId}/upload-cv`, file, 'cv'
		),

	// Download the original uploaded CV (streamed from GCS through the authenticated API)
	downloadOriginalCV: (candidateId: string) => {
		const token = useAuthStore.getState().token;
		return fetch(`${API_BASE_URL}/sourcing/candidates/${candidateId}/cv-file`, {
			headers: { Authorization: `Bearer ${token}` }
		}).then(async res => {
			if (!res.ok) throw new Error('CV download failed');
			const blob = await res.blob();
			const cd = res.headers.get('Content-Disposition') || '';
			const match = cd.match(/filename\*?=(?:UTF-8''|")?([^";]+)"?/i);
			const filename = match ? decodeURIComponent(match[1].replace(/"/g, '')) : null;
			return { blob, filename };
		});
	},
};

// Candidate AI (versioned discovery suggestions + saved-candidate rankings) —
// manual UI triggers; poll the list endpoints while a version is 'processing'.
export const candidateAiApi = {
	listSuggestions: (hiringRequestId: string) =>
		request<{ success: boolean; data: any[] }>(`/hiring-requests/${hiringRequestId}/candidate-suggestions`),

	generateSuggestions: (hiringRequestId: string, additionalContext?: string, chatId?: string) =>
		request<{ success: boolean; data: any }>(`/hiring-requests/${hiringRequestId}/candidate-suggestions/generate`, {
			method: 'POST',
			body: { ...(additionalContext ? { additionalContext } : {}), ...(chatId ? { chatId } : {}) }
		}),

	listRankings: (hiringRequestId: string) =>
		request<{ success: boolean; data: any[] }>(`/hiring-requests/${hiringRequestId}/candidate-rankings`),

	generateRanking: (hiringRequestId: string) =>
		request<{ success: boolean; data: any }>(`/hiring-requests/${hiringRequestId}/candidate-rankings/generate`, {
			method: 'POST',
			body: {}
		})
};

// Candidates (admin/TA management + public)
export const candidateApi = {
	list: (status?: string) => {
		const params = new URLSearchParams();
		if (status) params.append('status', status);
		return request<{ success: boolean; data: any[] }>(`/candidates?${params.toString()}`);
	},

	get: (id: string) =>
		request<{ success: boolean; data: { candidate: any; hiringRequests: any[] } }>(`/candidates/${id}`),

	generateLink: (sourcedCandidateId: string, jdType: string) =>
		request<{ success: boolean; data: { token: string; url: string; expiresAt: string } }>('/candidates/generate-link', {
			method: 'POST',
			body: { sourcedCandidateId, jdType }
		}),

	updateEmail: (id: string, email: string) =>
		request<{ success: boolean; data: { email: string } }>(`/candidates/${id}/email`, {
			method: 'PATCH',
			body: { email }
		}),

	getJobInfo: (token: string) =>
		request<{ success: boolean; data: any }>(`/candidates/job-info/${token}`),

	uploadCV: async (formData: FormData) => {
		const token = (await import('@/stores/auth')).useAuthStore.getState().token;
		const response = await fetch(`${API_BASE_URL}/candidates/upload-cv`, {
			method: 'POST',
			headers: token ? { 'Authorization': `Bearer ${token}` } : {},
			body: formData
		});
		const data: any = await response.json();
		if (!response.ok) throw new Error(data.error?.message || 'Upload failed');
		return data;
	}
};

// Access Link public helpers
export const accessLinkApi = {
	validateToken: (token: string) =>
		request<{ success: boolean; data: any }>(`/access-links/validate/${token}`),

	verifyEmail: (token: string, email: string) =>
		request<{ success: boolean; data: any }>('/access-links/verify-email', {
			method: 'POST',
			body: { token, email }
		}),

	listLinks: (hiringRequestId: string) =>
		request<{ success: boolean; data: any[] }>(`/access-links/hiring-request/${hiringRequestId}`)
};

// HM Portal (public, token-based)
export const hmPortalApi = {
	getJobs: (token: string) =>
		request<{ success: boolean; data: any[] }>(`/hm-portal/jobs?token=${token}`),

	getCandidates: (token: string, hiringRequestId: string) =>
		request<{ success: boolean; data: any[] }>(`/hm-portal/jobs/${hiringRequestId}/candidates?token=${token}`),

	addComment: (token: string, hiringRequestId: string, comment: string) =>
		request<{ success: boolean; data: any }>(`/hm-portal/jobs/${hiringRequestId}/comments?token=${token}`, {
			method: 'POST',
			body: { comment }
		}),

	getComments: (token: string, hiringRequestId: string) =>
		request<{ success: boolean; data: any[] }>(`/hm-portal/jobs/${hiringRequestId}/comments?token=${token}`),

	rateCandidate: (token: string, hiringRequestId: string, candidateId: string, rating: number, notes?: string) =>
		request<{ success: boolean; data: any }>(`/hm-portal/jobs/${hiringRequestId}/candidates/${candidateId}/rate?token=${token}`, {
			method: 'POST',
			body: { rating, notes }
		}),

	getRatings: (token: string, hiringRequestId: string) =>
		request<{ success: boolean; data: any[] }>(`/hm-portal/jobs/${hiringRequestId}/ratings?token=${token}`),

	addFeedback: (token: string, hiringRequestId: string, candidateId: string, feedback: string) =>
		request<{ success: boolean; data: any }>(`/hm-portal/jobs/${hiringRequestId}/candidates/${candidateId}/feedback?token=${token}`, {
			method: 'POST',
			body: { feedback }
		}),

	getFeedback: (token: string, hiringRequestId: string, candidateId: string) =>
		request<{ success: boolean; data: any[] }>(`/hm-portal/jobs/${hiringRequestId}/candidates/${candidateId}/feedback?token=${token}`)
};

// Outreach (candidate contact / WA messaging)
export const outreachApi = {
	create: (data: { sourcedCandidateId: string; hiringRequestId: string; contactedVia?: string; messageLanguage?: 'id' | 'en'; phone?: string; notes?: string; message?: string }) =>
		request<{ success: boolean; data: any }>('/outreach', {
			method: 'POST',
			body: data
		}),

	update: (id: string, data: { offerStatus?: string; notes?: string }) =>
		request<{ success: boolean; data: any }>(`/outreach/${id}`, {
			method: 'PATCH',
			body: data
		}),

	listByCandidate: (sourcedCandidateId: string) =>
		request<{ success: boolean; data: any[] }>(`/outreach/candidate/${sourcedCandidateId}`),

	listByHiringRequest: (hiringRequestId: string) =>
		request<{ success: boolean; data: any[] }>(`/outreach/hiring-request/${hiringRequestId}`),

	generateWAPreview: (data: {
		sourcedCandidateId: string; hiringRequestId: string; language: 'id' | 'en'; phone?: string;
	}) =>
		request<{ success: boolean; data: { message: string; waLink: string | null } }>('/outreach/wa-preview', {
			method: 'POST',
			body: data
		}),

	generateLinkedInPreview: (data: {
		sourcedCandidateId: string; hiringRequestId: string; language: 'id' | 'en';
	}) =>
		request<{ success: boolean; data: { message: string } }>('/outreach/linkedin-preview', {
			method: 'POST',
			body: data
		}),

	generateEmailPreview: (data: {
		sourcedCandidateId: string; hiringRequestId: string; language: 'id' | 'en';
	}) =>
		request<{ success: boolean; data: { subject: string; body: string; message: string } }>('/outreach/email-preview', {
			method: 'POST',
			body: data
		})
};

// Interview Notes
export const interviewNotesApi = {
	create: (data: { sourcedCandidateId: string; hiringRequestId: string; content: string }) =>
		request<{ success: boolean; data: any }>('/interview-notes', {
			method: 'POST',
			body: data
		}),

	update: (id: string, data: { content: string }) =>
		request<{ success: boolean; data: any }>(`/interview-notes/${id}`, {
			method: 'PATCH',
			body: data
		}),

	remove: (id: string) =>
		request<{ success: boolean }>(`/interview-notes/${id}`, {
			method: 'DELETE'
		}),

	listByCandidate: (sourcedCandidateId: string) =>
		request<{ success: boolean; data: any[] }>(`/interview-notes/candidate/${sourcedCandidateId}`),

	generateQuestions: (data: { sourcedCandidateId: string; hiringRequestId: string }) =>
		request<{ success: boolean; data: any }>('/interview-notes/generate-questions', {
			method: 'POST',
			body: data
		}),

	getQuestions: (sourcedCandidateId: string) =>
		request<{ success: boolean; data: any }>(`/interview-notes/questions/${sourcedCandidateId}`)
};

// CV Generation
export const cvGenerationApi = {
	generate: (data: { sourcedCandidateId: string; hiringRequestId: string; types: ('standard' | 'blind' | 'consultant_notes')[] }) =>
		request<{ success: boolean; data: any }>('/cv-generation/generate', {
			method: 'POST',
			body: data
		}),

	regenerate: (id: string, type: 'standard' | 'blind' | 'consultant_notes') =>
		request<{ success: boolean; data: any }>(`/cv-generation/${id}/regenerate`, {
			method: 'POST',
			body: { type }
		}),

	listByCandidate: (sourcedCandidateId: string) =>
		request<{ success: boolean; data: any[] }>(`/cv-generation/candidate/${sourcedCandidateId}`),

	getHistory: (id: string) =>
		request<{ success: boolean; data: any[] }>(`/cv-generation/${id}/history`),

	restore: (id: string) =>
		request<{ success: boolean; data: any }>(`/cv-generation/${id}/restore`, {
			method: 'POST'
		}),

	getById: (id: string) =>
		request<{ success: boolean; data: any }>(`/cv-generation/${id}`),

	updateContent: (id: string, content: string) =>
		request<{ success: boolean; data: any }>(`/cv-generation/${id}`, {
			method: 'PUT',
			body: { content }
		}),

	downloadPDF: (id: string) => {
		const token = useAuthStore.getState().token;
		return fetch(`${API_BASE_URL}/cv-generation/${id}/pdf`, {
			headers: { Authorization: `Bearer ${token}` }
		}).then(async res => {
			if (!res.ok) throw new Error('PDF download failed');
			const blob = await res.blob();
			const cd = res.headers.get('Content-Disposition') || '';
			const match = cd.match(/filename\*?=(?:UTF-8''|")?([^";]+)"?/i);
			const filename = match ? decodeURIComponent(match[1].replace(/"/g, '')) : null;
			return { blob, filename };
		});
	}
};

// --- CRM ---

export interface CrmContactListParams {
	page?: number;
	limit?: number;
	status?: string;
	sentiment?: string;
	ownerId?: string;
	companyId?: string;
	q?: string;
}

export interface CrmDealListParams {
	page?: number;
	limit?: number;
	status?: string;
	stage?: string;
	companyId?: string;
	ownerId?: string;
	q?: string;
}

export const crmContactsApi = {
	list: (params: CrmContactListParams = {}) => {
		const sp = new URLSearchParams();
		if (params.page) sp.append('page', String(params.page));
		if (params.limit) sp.append('limit', String(params.limit));
		if (params.status) sp.append('status', params.status);
		if (params.sentiment) sp.append('sentiment', params.sentiment);
		if (params.ownerId) sp.append('ownerId', params.ownerId);
		if (params.companyId) sp.append('companyId', params.companyId);
		if (params.q) sp.append('q', params.q);
		const qs = sp.toString();
		return request<{ success: boolean; data: any[]; meta: { page: number; limit: number; total: number; totalPages: number; hasMore: boolean } }>(
			`/crm/contacts${qs ? `?${qs}` : ''}`
		);
	},

	get: (id: string) =>
		request<{ success: boolean; data: any }>(`/crm/contacts/${id}`),

	create: (data: Record<string, unknown>) =>
		request<{ success: boolean; data: any }>('/crm/contacts', {
			method: 'POST',
			body: data
		}),

	update: (id: string, data: Record<string, unknown>) =>
		request<{ success: boolean; data: any }>(`/crm/contacts/${id}`, {
			method: 'PUT',
			body: data
		}),

	remove: (id: string) =>
		request<{ success: boolean }>(`/crm/contacts/${id}`, { method: 'DELETE' })
};

export const crmDealsApi = {
	list: (params: CrmDealListParams = {}) => {
		const sp = new URLSearchParams();
		if (params.page) sp.append('page', String(params.page));
		if (params.limit) sp.append('limit', String(params.limit));
		if (params.status) sp.append('status', params.status);
		if (params.stage) sp.append('stage', params.stage);
		if (params.companyId) sp.append('companyId', params.companyId);
		if (params.ownerId) sp.append('ownerId', params.ownerId);
		if (params.q) sp.append('q', params.q);
		const qs = sp.toString();
		return request<{ success: boolean; data: any[]; meta: { page: number; limit: number; total: number; totalPages: number; hasMore: boolean } }>(
			`/crm/deals${qs ? `?${qs}` : ''}`
		);
	},

	board: () =>
		request<{ success: boolean; data: any[] }>('/crm/deals/board'),

	get: (id: string) =>
		request<{ success: boolean; data: any }>(`/crm/deals/${id}`),

	// No standalone deal creation — detail records are opened automatically when a
	// hiring request is created (every hiring request IS a lead).

	update: (id: string, data: Record<string, unknown>) =>
		request<{ success: boolean; data: any }>(`/crm/deals/${id}`, {
			method: 'PUT',
			body: data
		}),

	updateStage: (id: string, stage: string) =>
		request<{ success: boolean; data: any }>(`/crm/deals/${id}/stage`, {
			method: 'PATCH',
			body: { stage }
		}),

	win: (id: string) =>
		request<{ success: boolean; data: any }>(`/crm/deals/${id}/win`, {
			method: 'POST'
		}),

	lose: (id: string, lostReason?: string) =>
		request<{ success: boolean; data: any }>(`/crm/deals/${id}/lose`, {
			method: 'POST',
			body: lostReason ? { lostReason } : undefined
		}),

	/** Create a new Hiring Request linked to this deal. The deal's company is
	 *  prefilled; callers should still provide roleTitle, yearsOfExperience,
	 *  directReports, meetingNotes, and any PIC overrides. */
	addHiringRequest: (id: string, body: Record<string, any>) =>
		request<{ success: boolean; data: { hiringRequest: any; deal: any } }>(`/crm/deals/${id}/hiring-requests`, {
			method: 'POST',
			body
		}),

	// Deal ↔ multiple contacts
	addDealContact: (id: string, contactId: string) =>
		request<{ success: boolean; data: any }>(`/crm/deals/${id}/contacts`, { method: 'POST', body: { contactId } }),
	removeDealContact: (id: string, contactId: string) =>
		request<{ success: boolean; data: any }>(`/crm/deals/${id}/contacts/${contactId}`, { method: 'DELETE' }),
	setPrimaryDealContact: (id: string, contactId: string) =>
		request<{ success: boolean; data: any }>(`/crm/deals/${id}/contacts/${contactId}/primary`, { method: 'POST' }),

	remove: (id: string) =>
		request<{ success: boolean }>(`/crm/deals/${id}`, { method: 'DELETE' })
};

export interface CrmActivityListParams {
	contactId?: string;
	companyId?: string;
	dealId?: string;
	limit?: number;
}

export const crmActivitiesApi = {
	list: (params: CrmActivityListParams = {}) => {
		const sp = new URLSearchParams();
		if (params.contactId) sp.append('contactId', params.contactId);
		if (params.companyId) sp.append('companyId', params.companyId);
		if (params.dealId) sp.append('dealId', params.dealId);
		if (params.limit) sp.append('limit', String(params.limit));
		const qs = sp.toString();
		return request<{ success: boolean; data: any[] }>(`/crm/activities${qs ? `?${qs}` : ''}`);
	},

	create: (data: Record<string, unknown>) =>
		request<{ success: boolean; data: any }>('/crm/activities', {
			method: 'POST',
			body: data
		}),

	update: (id: string, data: Record<string, unknown>) =>
		request<{ success: boolean; data: any }>(`/crm/activities/${id}`, {
			method: 'PUT',
			body: data
		}),

	remove: (id: string) =>
		request<{ success: boolean }>(`/crm/activities/${id}`, { method: 'DELETE' })
};

// Users (Super Admin management)
export const usersApi = {
	list: () =>
		request<{ success: boolean; data: any[] }>('/users'),

	listShareable: () =>
		request<{ success: boolean; data: any[] }>('/users/shareable'),

	block: (id: string, reason: string) =>
		request<{ success: boolean; data: any }>(`/users/${id}/block`, {
			method: 'PATCH',
			body: { reason }
		}),

	unblock: (id: string) =>
		request<{ success: boolean; data: any }>(`/users/${id}/unblock`, {
			method: 'PATCH'
		}),

	changeRole: (id: string, role: string) =>
		request<{ success: boolean; data: any }>(`/users/${id}/role`, {
			method: 'PATCH',
			body: { role }
		})
};
